import { useActiveCircle } from '@rhg/hooks/use-active-circle';
import { useGuidePlayer } from '@rhg/hooks/use-guide-player';
import { useProgram } from '@rhg/hooks/use-program';
import useRecipientGuide from '@rhg/hooks/use-recipient-guide';
import { useWorkflowsForRecipientByType } from '@rhg/hooks/use-workflows';
import {
    getIsJoined,
    getProgramPackClassFromMetadata,
    getProgramRoute,
} from '@rhg/utils/programs';
import { getWorkflowPackClass } from '@rhg/utils/workflows';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useRef } from 'react';

function LoadGuide({
    packClass: _providedPackClass,
    programId,
    guideId,
}: {
    packClass: string;
    programId: string;
    guideId: string;
}) {
    const { activeCircleId } = useActiveCircle();
    const { program, isFetched: isFetchedProgram } = useProgram(
        programId,
        _providedPackClass,
        activeCircleId
    );
    const packClass =
        getProgramPackClassFromMetadata(program) ?? _providedPackClass;
    const { guide, isFetched: isFetchedGuide } = useRecipientGuide({
        guideId,
        programId,
        packClass,
    });
    const { workflows, isFetched: isFetchedWorkflow } =
        useWorkflowsForRecipientByType({
            circleId: activeCircleId,
            workflowType: 'clinical_program',
        });

    const workflow = useMemo(() => {
        return workflows?.find(
            (workflow) => getWorkflowPackClass(workflow) === packClass
        );
    }, [workflows, packClass]);
    const { push } = useRouter();

    const { openGuidePlayer } = useGuidePlayer();
    const hasOpenedPlayer = useRef<boolean>(false);

    useEffect(() => {
        if (
            isFetchedProgram &&
            isFetchedGuide &&
            isFetchedWorkflow &&
            program &&
            guide &&
            hasOpenedPlayer.current !== true
        ) {
            if (workflow) {
                const isJoined = getIsJoined(workflow);
                if (!isJoined) {
                    const url = getProgramRoute(program, packClass);
                    push(url);
                    return;
                }
            }

            openGuidePlayer({
                program,
                guide,
                packClass: _providedPackClass,
                workflow,
            });
            hasOpenedPlayer.current = true;
        }
    }, [
        isFetchedProgram,
        isFetchedGuide,
        isFetchedWorkflow,
        program,
        guide,
        workflow,
        openGuidePlayer,
        packClass,
        _providedPackClass,
        push,
    ]);

    return null;
}

export function DeepLinkGuideManager() {
    const { query } = useRouter();
    const {
        pack_class: packClass,
        program_id: programId,
        guide_id: guideId,
    } = query || {};

    if (
        typeof packClass !== 'string' ||
        typeof programId !== 'string' ||
        typeof guideId !== 'string'
    ) {
        return null;
    }

    return (
        <LoadGuide
            packClass={packClass}
            programId={programId}
            guideId={guideId}
        />
    );
}
